import { faWaveformLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSelector } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { ComponentProps } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { AppState, useSelector } from '@/redux/helper';
import { AmbientModeBackgroundMusic } from '@/redux/slices/remoteConfig/types';
import { setLastSelectedBackgroundMusicURL } from '@/redux/slices/settings/ambient/action';

interface BackgroundMusicSelectorProps extends ComponentProps<'div'> {
  muted: boolean;
  volume: number;
}

const BackgroundMusicSelector = ({ muted, volume, className, ...props }: BackgroundMusicSelectorProps) => {
  const dispatch = useDispatch();
  const lastSelectedBackgroundMusicURL = useSelector((state) => state.settings.ambientMode.lastSelectedBackgroundMusicURL);
  const selectMusics = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.remoteConfig.ambientModeBackgroundMusics,
        (_: AppState, targetURL: string | undefined) => targetURL,
        (musics, targetURL) => {
          if (!targetURL) {
            return [musics[0], musics.slice(1)] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]];
          }
          const result = musics.reduce(
            (acc, item) => {
              if (item.url === targetURL) {
                acc[0] = item;
              } else {
                acc[1].push(item);
              }
              return acc;
            },
            [undefined, []] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]],
          );
          if (!result[0]) {
            return [musics[0], musics.slice(1)] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]];
          }
          return result;
        },
      ),
    [],
  );
  const [selectedMusicURL, setSelectedMusicURL] = useState(lastSelectedBackgroundMusicURL);
  const [selectedMusic, otherMusics] = useSelector((state) => selectMusics(state, selectedMusicURL));

  const [isExpanded, setIsExpanded] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const safeVolume = Math.max(0, Math.min(1, volume));

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!muted && selectedMusic?.url && audioElement) {
      audioElement.volume = safeVolume;
      audioElement.play().catch((error) => {
        console.error('Failed to play background music:', error);
      });
    }

    return () => {
      if (audioElement) {
        audioElement.pause();
      }
    };
  }, [muted, selectedMusic?.url, safeVolume]);

  return (
    <div
      aria-expanded={isExpanded}
      className={clsx('group/bgm-selector pointer-events-none aria-expanded:pointer-events-auto', className)}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <audio ref={audioRef} src={selectedMusic?.url} loop className="hidden" />
      <ul className="preflight preflight-ul pointer-events-none mb-2 flex flex-col gap-2 opacity-0 transition-opacity group-aria-expanded/bgm-selector:pointer-events-auto group-aria-expanded/bgm-selector:opacity-100">
        {otherMusics.map((music) => (
          <li key={music.url}>
            <button
              type="button"
              className="preflight preflight-button flex items-center gap-2"
              onClick={() => {
                setSelectedMusicURL(music.url);
                setIsExpanded(false);
                dispatch(setLastSelectedBackgroundMusicURL(music.url));
              }}
            >
              <img src={music.image} alt={music.name} className="preflight preflight-img size-12 rounded-md" />
              <div className="min-w-24 rounded-full bg-white/60 px-3 py-1 text-center text-xs font-medium text-black backdrop-blur-[2px]">
                {music.name}
              </div>
            </button>
          </li>
        ))}
      </ul>
      {selectedMusic && (
        <button
          type="button"
          className="preflight preflight-button flex items-center gap-2"
          onClick={() => {
            setIsExpanded(false);
          }}
        >
          <div className="pointer-events-auto relative size-12 overflow-clip rounded-md">
            <img
              src={selectedMusic.image}
              alt={selectedMusic.name}
              className="preflight preflight-img size-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/25 text-white">
              <FontAwesomeIcon icon={faWaveformLines} className="!size-9" />
            </div>
          </div>
          <div className="pointer-events-none min-w-24 rounded-full bg-white/60 px-3 py-1 text-center text-xs font-medium text-black opacity-0 backdrop-blur-[2px] transition-opacity group-aria-expanded/bgm-selector:pointer-events-auto group-aria-expanded/bgm-selector:opacity-100">
            {selectedMusic.name}
          </div>
        </button>
      )}
    </div>
  );
};

export default BackgroundMusicSelector;
